"use client"

import React, { useState } from "react"
import Stepper, { StepperStep } from "@/app/components/ui/Stepper"
import { motion } from "framer-motion"

interface Step {
  title: string
  description?: string
  content?: string
  icon?: string
}

interface ProductStepsProps {
  data: {
    title?: string
    subtitle?: string
    steps?: Step[]
    description?: string
    orientation?: "horizontal" | "vertical"
    variant?: "default" | "compact" | "detailed"
  }
}

const ProductSteps: React.FC<ProductStepsProps> = ({ data }) => {
  const [activeStep, setActiveStep] = useState(0)

  if (!data.steps || data.steps.length === 0) {
    return null
  }

  // Transform steps data to Stepper format
  const stepperSteps: StepperStep[] = data.steps.map((step, index) => ({
    id: index,
    label: step.title,
    description: step.description,
    content: step.content ? (
      <div
        className="prose prose-invert max-w-none text-sm md:text-base"
        dangerouslySetInnerHTML={{
          __html: step.content,
        }}
      />
    ) : undefined,
  }))

  // Orientation is now handled responsively in Stepper component
  // Default to horizontal - Stepper will switch to vertical on mobile automatically
  const orientation: "horizontal" | "vertical" = data.orientation || "horizontal"

  return (
    <section className="relative py-4 md:py-6 lg:py-8">
      {/* Background decorative elements */}
      {/* <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-secondary/5 rounded-full blur-3xl" />
      </div> */}

      <div className="container mx-auto relative z-10">
        {/* Section Header */}
        {(data.title || data.subtitle) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.5 }}
            className="text-center mb-5 lg:mb-12"
          >
            {data.title && (
              <h2 className="section-title !mb-2 md:mb-4">{data.title}</h2>
            )}
            {data.subtitle && (
              <p className="text-base md:text-lg text-white/70 max-w-2xl mx-auto">
                {data.subtitle}
              </p>
            )}
          </motion.div>
        )}

        {/* Stepper Component */}
        <div className="max-w-5xl mx-auto">
          <Stepper
            steps={stepperSteps}
            activeStep={activeStep}
            onStepChange={setActiveStep}
            orientation={orientation}
            variant={data.variant || "detailed"}
            clickable={true}
            showContent={true}
            size={orientation === "horizontal" ? "md" : "md"}
            className="w-full"
          />
        </div>

        {/* Description if provided */}
        {data.description && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mt-12 p-6 md:p-8 rounded-2xl bg-white/[0.02] border border-white/10 backdrop-blur-sm max-w-3xl mx-auto"
          >
            <div
              className="prose prose-invert max-w-none text-center text-white/80"
              dangerouslySetInnerHTML={{
                __html: data.description,
              }}
            />
          </motion.div>
        )}
      </div>
    </section>
  )
}

export default ProductSteps
